import React, { useState, useEffect } from 'react';
import { Users, Star, TrendingUp, MessageSquare, Award, RefreshCw, Calendar, ChevronDown } from 'lucide-react';
import { getDynamicBusinessFeedbackReport, BusinessFeedbackReport, ResourceFeedbackData, getFeedbackResponseRate } from '../lib/feedback';
import { getAllFeedbackFormFields, type FeedbackFormField } from '../lib/feedbackForm';
import { useBusiness } from '../contexts/BusinessContext';
import PermissionGuard from '../components/PermissionGuard';

interface StarRatingProps {
  rating: number;
  size?: 'small' | 'medium' | 'large';
  showValue?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({ rating, size = 'medium', showValue = false }) => {
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-5 w-5',
    large: 'h-6 w-6'
  };

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="flex items-center space-x-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= fullStars
                ? 'text-yellow-400 fill-current'
                : star === fullStars + 1 && hasHalfStar
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
      {showValue && (
        <span className="text-sm font-medium text-gray-700 ml-2">{rating.toFixed(1)}</span>
      )}
    </div>
  );
};

interface ResourceCardProps {
  resource: ResourceFeedbackData;
  formFields: FeedbackFormField[];
}

const ResourceCard: React.FC<ResourceCardProps> = ({ resource, formFields }) => {
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Get rating fields for display
  const ratingFields = formFields.filter(field => field.field_type === 'rating');

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Avatar */}
          <div className="relative">
            {resource.avatar_url ? (
              <img
                src={resource.avatar_url}
                alt={resource.resource_name}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-medium">
                {getInitials(resource.first_name, resource.last_name)}
              </div>
            )}
          </div>

          {/* Resource Info */}
          <div>
            <h3 className="font-medium text-gray-900">{resource.resource_name}</h3>
            <p className="text-sm text-gray-500">{resource.total_feedback_count} feedback{resource.total_feedback_count !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Rating */}
        <div className="flex items-center space-x-3">
          <StarRating rating={resource.overall_average_rating} size="small" />
          <span className="text-lg font-bold text-gray-900">{resource.overall_average_rating.toFixed(1)}</span>
        </div>
      </div>

      {/* Dynamic detailed ratings or fallback to legacy display */}
      <div className="mt-3">
        {ratingFields.length > 0 ? (
          // Show dynamic fields if available
          <div className={`grid gap-3 text-xs ${
            ratingFields.length === 1 ? 'grid-cols-1' : 
            ratingFields.length === 2 ? 'grid-cols-2' : 
            'grid-cols-3'
          }`}>
            {ratingFields.slice(0, 3).map((field) => (
              <div key={field.id} className="bg-gray-50 rounded p-2">
                <div className="text-gray-600 mb-1 truncate" title={field.field_label}>
                  {field.field_label}
                </div>
                <div className="flex items-center space-x-1">
                  <StarRating rating={resource.overall_average_rating} size="small" />
                  <span className="font-medium">{resource.overall_average_rating.toFixed(1)}</span>
                </div>
              </div>
            ))}
            {ratingFields.length > 3 && (
              <div className="bg-gray-50 rounded p-2 flex items-center justify-center">
                <span className="text-xs text-gray-500">+{ratingFields.length - 3} more</span>
              </div>
            )}
          </div>
        ) : (
          // Fallback to legacy display for backward compatibility
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-600 mb-1">Quality</div>
              <div className="flex items-center space-x-1">
                <StarRating rating={resource.average_quality_rating} size="small" />
                <span className="font-medium">{resource.average_quality_rating.toFixed(1)}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-600 mb-1">Experience</div>
              <div className="flex items-center space-x-1">
                <StarRating rating={resource.average_experience_rating} size="small" />
                <span className="font-medium">{resource.average_experience_rating.toFixed(1)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Date range filter types
type DateRangeOption = 'this_month' | 'last_month';

interface DateRangeFilter {
  option: DateRangeOption;
  label: string;
  startDate: string;
  endDate: string;
}

// Helper function to get date ranges
const getDateRanges = (): Record<DateRangeOption, DateRangeFilter> => {
  const now = new Date();
  
  // This month
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  
  // Last month
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  
  return {
    this_month: {
      option: 'this_month',
      label: 'This Month',
      startDate: thisMonthStart.toISOString(),
      endDate: thisMonthEnd.toISOString()
    },
    last_month: {
      option: 'last_month',
      label: 'Last Month',
      startDate: lastMonthStart.toISOString(),
      endDate: lastMonthEnd.toISOString()
    }
  };
};

export default function TeamReports() {
  const { selectedBusiness } = useBusiness();
  const [feedbackReport, setFeedbackReport] = useState<BusinessFeedbackReport | null>(null);
  const [formFields, setFormFields] = useState<FeedbackFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>('this_month');
  const [isDateRangeDropdownOpen, setIsDateRangeDropdownOpen] = useState(false);
  const [feedbackResponseRate, setFeedbackResponseRate] = useState<{
    responseRate: number;
    completedBookings: number;
    feedbackSubmissions: number;
  } | null>(null);

  const dateRanges = getDateRanges();

  const loadFeedbackReport = async () => {
    if (!selectedBusiness?.id) return;

    setLoading(true);
    setError(null);

    try {
      const selectedRange = dateRanges[selectedDateRange];
      const dateRangeFilter = {
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate
      };

      console.log('Loading feedback report with date range:', {
        option: selectedDateRange,
        ...dateRangeFilter
      });

      // Load feedback report, form fields, and response rate in parallel
      const [report, fields, responseRate] = await Promise.all([
        getDynamicBusinessFeedbackReport(selectedBusiness.id, dateRangeFilter),
        getAllFeedbackFormFields(selectedBusiness.id),
        getFeedbackResponseRate(selectedBusiness.id, dateRangeFilter)
      ]);
      
      setFeedbackReport(report);
      setFormFields(fields);
      setFeedbackResponseRate(responseRate);
    } catch (err) {
      console.error('Error loading feedback report:', err);
      setError('Failed to load feedback report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedbackReport();
  }, [selectedBusiness?.id, selectedDateRange]);

  const handleDateRangeChange = (option: DateRangeOption) => {
    setSelectedDateRange(option);
    setIsDateRangeDropdownOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="h-6 w-6 mr-3 text-blue-600" />
              Team Reports
            </h1>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-white rounded-lg border border-gray-200 p-12">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-500">Loading feedback report...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="h-6 w-6 mr-3 text-blue-600" />
              Team Reports
            </h1>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-white rounded-lg border border-gray-200 p-12">
            <div className="text-center">
              <MessageSquare className="h-8 w-8 text-red-400 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadFeedbackReport}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="teamReports.pageAccess">
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gray-100 border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Users className="h-6 w-6 mr-3 text-blue-600" />
                  Team Reports - Customer Feedback
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Track team performance and customer satisfaction metrics
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Date Range Filter */}
                <div className="relative">
                  <button
                    onClick={() => setIsDateRangeDropdownOpen(!isDateRangeDropdownOpen)}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-gray-300"
                  >
                    <Calendar className="h-4 w-4" />
                    <span>{dateRanges[selectedDateRange].label}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  
                  {isDateRangeDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                      <div className="py-1">
                        {Object.entries(dateRanges).map(([key, range]) => (
                          <button
                            key={key}
                            onClick={() => handleDateRangeChange(key as DateRangeOption)}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                              selectedDateRange === key ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                            }`}
                          >
                            {range.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Refresh Button */}
                <button
                  onClick={loadFeedbackReport}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Blank Report Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Card 1 - Feedback % */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Feedback %</h3>
                    <p className="text-xs text-gray-500">Response rate from completed bookings</p>
                  </div>
                </div>
              </div>
              
              {feedbackResponseRate ? (
                <div className="space-y-3">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-1">
                      {feedbackResponseRate.responseRate}%
                    </div>
                    <p className="text-sm text-gray-500">Response Rate</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                    <div className="text-center">
                      <div className="text-lg font-semibold text-gray-900">
                        {feedbackResponseRate.completedBookings}
                      </div>
                      <p className="text-xs text-gray-500">Completed Bookings</p>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-green-600">
                        {feedbackResponseRate.feedbackSubmissions}
                      </div>
                      <p className="text-xs text-gray-500">Feedback Received</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-3xl font-bold text-gray-200 mb-2">--</div>
                  <p className="text-sm text-gray-400">
                    {loading ? 'Loading...' : 'No data available'}
                  </p>
                </div>
              )}
            </div>

            {/* Card 2 - Overall Team Rating */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Award className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Overall Team Rating</h3>
                    <p className="text-xs text-gray-500">Average customer rating</p>
                  </div>
                </div>
              </div>
              
              {feedbackReport ? (
                <div className="space-y-3">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-1">
                      {feedbackReport.overall_business_rating.toFixed(1)}
                    </div>
                    <div className="flex items-center justify-center mb-2">
                      <StarRating rating={feedbackReport.overall_business_rating} size="small" />
                    </div>
                    <p className="text-sm text-gray-500">Overall Rating</p>
                  </div>
                  
                  <div className="text-center pt-3 border-t border-gray-100">
                    <div className="text-lg font-semibold text-gray-900">
                      {feedbackReport.total_feedback_count}
                    </div>
                    <p className="text-xs text-gray-500">
                      Customer feedback{feedbackReport.total_feedback_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-3xl font-bold text-gray-200 mb-2">--</div>
                  <p className="text-sm text-gray-400">
                    {loading ? 'Loading...' : 'No data available'}
                  </p>
                </div>
              )}
            </div>

            {/* Card 3 - Response Rate */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Response Rate</h3>
                    <p className="text-xs text-gray-500">Feedback response rate</p>
                  </div>
                </div>
              </div>
              <div className="text-center py-8">
                <div className="text-3xl font-bold text-gray-200 mb-2">--</div>
                <p className="text-sm text-gray-400">Coming Soon</p>
              </div>
            </div>
          </div>

          {/* Individual Resource Performance */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Award className="h-5 w-5 mr-2 text-green-600" />
                Individual Team Member Performance
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Customer feedback ratings for each team member ({dateRanges[selectedDateRange].label.toLowerCase()})
              </p>
            </div>

            <div className="p-6">
              {feedbackReport?.resources && feedbackReport.resources.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {feedbackReport.resources.map((resource) => (
                    <ResourceCard key={resource.resource_id} resource={resource} formFields={formFields} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    No Feedback Data for {dateRanges[selectedDateRange].label}
                  </h4>
                  <p className="text-gray-500 max-w-sm mx-auto">
                    No customer feedback was submitted during this period. Try selecting a different date range or complete more bookings to see feedback reports.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Click outside to close dropdown */}
        {isDateRangeDropdownOpen && (
          <div 
            className="fixed inset-0 z-0" 
            onClick={() => setIsDateRangeDropdownOpen(false)}
          />
        )}
      </div>
    </PermissionGuard>
  );
} 
 
 
 
 